/**
 * Date and layout maths of `NxTimeBar`, without the component.
 *
 * Days are ISO `YYYY-MM-DD` strings throughout. Distances between days are
 * counted on UTC day numbers, so no DST hour can shift a bar by a day. Month
 * boundaries and ISO week numbers come from the calendar's tested helpers
 * (`Calendar/calendarGrid.ts`) instead of being rebuilt here.
 */
import { isIsoDate, isoWeekNumber, parseIso, toIso } from '../Calendar/calendarGrid';
import type { NxHue } from '../lib/hue';

/** Key of a row, group, item or milestone. Compared with `===`. */
export type NxTimeBarKey = string | number;

/** Axis unit of the header: one column per day, or weeks for longer ranges. */
export type NxTimeBarScale = 'day' | 'week';

/** Intent colour of a bar or a group: when the colour means something (late = danger). */
export type NxTimeBarTone = 'brand' | 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** A group header (a project phase): its rows follow it. */
export interface NxTimeBarGroup {
  id: NxTimeBarKey;
  label: string;
  /** Muted second line (dates, owner, …). */
  sublabel?: string;
  /** Accent strip of the header and colour of the span bar. */
  tone?: NxTimeBarTone;
  /** Category hue instead of an intent tone; wins over `tone`. */
  hue?: NxHue;
}

/** One row of the chart: a task, an issue, a person. */
export interface NxTimeBarRow {
  id: NxTimeBarKey;
  label: string;
  /** Muted second line (assignee, customer, …). */
  sublabel?: string;
  /** Short code in front of the label (an issue key) — tells kinds of rows apart. */
  tag?: string;
  /** The group (phase) the row belongs to. Rows without a known group come first. */
  groupId?: NxTimeBarKey;
}

/** A bar from `start` to `end` (both inclusive) in one row. */
export interface NxTimeBarItem {
  id: NxTimeBarKey;
  rowId: NxTimeBarKey;
  /** First day, ISO `YYYY-MM-DD` (a datetime is cut to its date part). */
  start: string;
  /** Last day, inclusive. Missing or invalid: a one-day bar. Before `start`: swapped. */
  end: string;
  /** Name of the item: tooltip and accessible name. */
  label: string;
  /** Text inside the bar (planned hours „8 h“); defaults to `label`, `''` shows none. */
  text?: string;
  /** Extra words for the tooltip and the accessible name, e.g. the status („In Arbeit“). */
  detail?: string;
  tone?: NxTimeBarTone;
  /** Category hue instead of an intent tone; wins over `tone`. */
  hue?: NxHue;
  /** Draws the red „Verzug“ frame. The caller decides what is late. */
  delayed?: boolean;
}

/** A diamond on one day: on a row (`rowId`) or on a group header (`groupId`). */
export interface NxTimeBarMilestone {
  id: NxTimeBarKey;
  /** The day, ISO `YYYY-MM-DD`. */
  date: string;
  label: string;
  /** Row the diamond sits in; wins over `groupId`. */
  rowId?: NxTimeBarKey;
  /** Group header the diamond sits on. */
  groupId?: NxTimeBarKey;
  /** Extra words for the tooltip and the accessible name. */
  detail?: string;
  /** Missed: drawn red (wins over `done`). */
  missed?: boolean;
  /** Reached: drawn green. */
  done?: boolean;
}

/** What a legend swatch looks like. */
export type NxTimeBarLegendKind =
  | 'bar'
  | 'delay'
  | 'milestone'
  | 'milestone-missed'
  | 'milestone-done'
  | 'today'
  | 'non-working';

export interface NxTimeBarLegendEntry {
  label: string;
  /** Default `bar`: a swatch in `tone` / `hue`. */
  kind?: NxTimeBarLegendKind;
  tone?: NxTimeBarTone;
  hue?: NxHue;
}

/** Longest range the header labels day by day; longer ranges switch to weeks. */
export const TIME_BAR_DAY_SCALE_MAX_DAYS = 42;

/**
 * Hard cap of the visible range (three years). A typo in one date of a fitted
 * range („2062“) would otherwise build tens of thousands of columns.
 */
export const TIME_BAR_MAX_DAYS = 3 * 366;

/** Air around the data when the range is fitted. */
const FIT_PADDING_DAYS = 2;

/** Days shown when nothing dated is there to fit: four weeks from this Monday. */
const EMPTY_RANGE_DAYS = 28;

const MS_PER_DAY = 86_400_000;

/** An ISO day from an ISO day or datetime; `null` for anything that is no real date. */
export function toIsoDay(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const day = value.slice(0, 10);
  return isIsoDate(day) ? day : null;
}

/** Days since 1970-01-01, counted in UTC so a DST switch cannot move a day. */
function dayNumber(iso: string): number {
  const { year, month, day } = parseIso(iso);
  return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

function fromDayNumber(value: number): string {
  const date = new Date(value * MS_PER_DAY);
  return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

/** 0 = Monday … 6 = Sunday. Day 0 (1970-01-01) was a Thursday. */
function weekdayOfNumber(value: number): number {
  return (((value + 3) % 7) + 7) % 7;
}

/** Whole days from `from` to `to`; negative when `to` lies before `from`. */
export function daysBetween(from: string, to: string): number {
  return dayNumber(to) - dayNumber(from);
}

export function addIsoDays(iso: string, days: number): string {
  return fromDayNumber(dayNumber(iso) + days);
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayOf(iso: string): number {
  return weekdayOfNumber(dayNumber(iso));
}

export function resolveTimeBarScale(
  dayCount: number,
  scale?: NxTimeBarScale | null
): NxTimeBarScale {
  return scale ?? (dayCount > TIME_BAR_DAY_SCALE_MAX_DAYS ? 'week' : 'day');
}

/** A visible range, both ends inclusive. */
export interface NxTimeBarRange {
  start: string;
  end: string;
}

/**
 * The range that shows all items and milestones: whole weeks (Monday to
 * Sunday) with at least two days of air on either side. Without a single valid
 * date it is the four weeks from the Monday of `today`'s week.
 */
export function fitTimeBarRange(
  items: readonly Pick<NxTimeBarItem, 'start' | 'end'>[],
  milestones: readonly Pick<NxTimeBarMilestone, 'date'>[],
  today: string
): NxTimeBarRange {
  let min = Infinity;
  let max = -Infinity;
  const take = (value: string | undefined) => {
    const iso = toIsoDay(value);
    if (!iso) return;
    const n = dayNumber(iso);
    min = Math.min(min, n);
    max = Math.max(max, n);
  };
  for (const item of items) {
    take(item.start);
    take(item.end);
  }
  for (const milestone of milestones) take(milestone.date);

  if (min === Infinity) {
    const monday = dayNumber(today) - weekdayOf(today);
    return { start: fromDayNumber(monday), end: fromDayNumber(monday + EMPTY_RANGE_DAYS - 1) };
  }
  const from = min - FIT_PADDING_DAYS;
  const to = max + FIT_PADDING_DAYS;
  return {
    start: fromDayNumber(from - weekdayOfNumber(from)),
    end: fromDayNumber(to + 6 - weekdayOfNumber(to)),
  };
}

/**
 * The range the chart shows: `start`/`end` where they are valid days, the
 * fitted range for what is missing, in order, and at most `TIME_BAR_MAX_DAYS`.
 */
export function resolveTimeBarRange(options: {
  start?: string | null;
  end?: string | null;
  items: readonly Pick<NxTimeBarItem, 'start' | 'end'>[];
  milestones?: readonly Pick<NxTimeBarMilestone, 'date'>[];
  today: string;
}): NxTimeBarRange {
  const start = toIsoDay(options.start);
  const end = toIsoDay(options.end);
  const fitted =
    start && end
      ? { start, end }
      : fitTimeBarRange(options.items, options.milestones ?? [], options.today);
  let from = start ?? fitted.start;
  let to = end ?? fitted.end;
  if (daysBetween(from, to) < 0) [from, to] = [to, from];
  if (daysBetween(from, to) >= TIME_BAR_MAX_DAYS) to = addIsoDays(from, TIME_BAR_MAX_DAYS - 1);
  return { start: from, end: to };
}

// ── Formatting ────────────────────────────────────────────────────────────────

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
}

function utcDate(iso: string): Date {
  return new Date(dayNumber(iso) * MS_PER_DAY);
}

const FULL_DATE: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
const DAY_MONTH: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit' };

/**
 * „15.09.2026“ for one day, „01.10.–05.10.2026“ within a year,
 * „28.12.2026–05.01.2027“ across the turn of the year.
 */
export function formatTimeBarRange(start: string, end: string, locale = 'de-DE'): string {
  const full = formatter(locale, FULL_DATE);
  if (start === end) return full.format(utcDate(start));
  const head =
    start.slice(0, 4) === end.slice(0, 4)
      ? formatter(locale, DAY_MONTH).format(utcDate(start))
      : full.format(utcDate(start));
  return `${head}–${full.format(utcDate(end))}`;
}

// ── Axis ──────────────────────────────────────────────────────────────────────

export interface TimeBarDay {
  iso: string;
  /** 0 = Monday … 6 = Sunday. */
  weekday: number;
  /** Weekend (or what `nonWorking` says) or a holiday: hatched. */
  nonWorking: boolean;
  /** Name of the holiday on this day. */
  holiday?: string;
}

/** A header cell over `span` day columns from `start`. */
export interface TimeBarSpan {
  key: string;
  label: string;
  sublabel?: string;
  /** First day column, 0-based. */
  start: number;
  span: number;
  /** Holds today. */
  current: boolean;
  /** Hatched (a non-working day in the day scale). */
  nonWorking: boolean;
  /** Tooltip, e.g. the holiday's name. */
  title?: string;
}

export interface TimeBarAxis extends NxTimeBarRange {
  scale: NxTimeBarScale;
  days: TimeBarDay[];
  /** Upper header row: weeks in the day scale, months in the week scale. */
  bands: TimeBarSpan[];
  /** Lower header row: days in the day scale, weeks in the week scale. */
  units: TimeBarSpan[];
  /** Consecutive non-working days, one shaded strip each. */
  nonWorkingRuns: { start: number; span: number }[];
  /** Day columns a new week starts at (Mondays), without the first column. */
  weekStarts: number[];
  /** Column of today, `null` when today is not visible. */
  todayIndex: number | null;
}

export interface TimeBarAxisOptions extends NxTimeBarRange {
  scale?: NxTimeBarScale | null;
  today?: string | null;
  /** ISO day → name. Holidays are non-working and named in the header tooltip. */
  holidays?: Readonly<Record<string, string>>;
  /** Replaces the weekend rule (Saturday, Sunday). Holidays count on top. */
  nonWorking?: (iso: string) => boolean;
  locale?: string;
  weekLabel?: (week: number) => string;
}

/** Split day columns into runs that share a key (weeks, months). */
function runs(days: TimeBarDay[], keyOf: (day: TimeBarDay) => string) {
  const out: { key: string; start: number; span: number }[] = [];
  days.forEach((day, index) => {
    const key = keyOf(day);
    const last = out[out.length - 1];
    if (last && last.key === key) last.span += 1;
    else out.push({ key, start: index, span: 1 });
  });
  return out;
}

function isoWeekOf(iso: string): number {
  const { year, month, day } = parseIso(iso);
  return isoWeekNumber(year, month, day);
}

/** Monday of the week `iso` lies in. */
function mondayOf(iso: string): string {
  return addIsoDays(iso, -weekdayOf(iso));
}

export function buildTimeBarAxis(options: TimeBarAxisOptions): TimeBarAxis {
  const {
    start,
    end,
    holidays,
    nonWorking,
    locale = 'de-DE',
    weekLabel = (week: number) => `KW ${week}`,
  } = options;
  const first = dayNumber(start);
  const dayCount = Math.max(1, dayNumber(end) - first + 1);
  const scale = resolveTimeBarScale(dayCount, options.scale);
  const todayOffset = options.today ? dayNumber(options.today) - first : -1;
  const todayIndex = todayOffset >= 0 && todayOffset < dayCount ? todayOffset : null;
  const holds = (from: number, span: number) =>
    todayIndex !== null && todayIndex >= from && todayIndex < from + span;

  const days: TimeBarDay[] = [];
  for (let index = 0; index < dayCount; index += 1) {
    const iso = fromDayNumber(first + index);
    const weekday = weekdayOfNumber(first + index);
    const holiday = holidays?.[iso];
    const off = (nonWorking ? nonWorking(iso) : weekday >= 5) || !!holiday;
    days.push(
      holiday ? { iso, weekday, nonWorking: off, holiday } : { iso, weekday, nonWorking: off }
    );
  }

  const dayMonth = formatter(locale, DAY_MONTH);
  const weeks = runs(days, (day) => mondayOf(day.iso));
  let bands: TimeBarSpan[];
  let units: TimeBarSpan[];

  if (scale === 'day') {
    const weekday = formatter(locale, { weekday: 'short' });
    bands = weeks.map((week) => {
      const firstDay = days[week.start].iso;
      const lastDay = days[week.start + week.span - 1].iso;
      const from = dayMonth.format(utcDate(firstDay));
      return {
        key: week.key,
        label: weekLabel(isoWeekOf(firstDay)),
        sublabel: week.span > 1 ? `${from}–${dayMonth.format(utcDate(lastDay))}` : from,
        start: week.start,
        span: week.span,
        current: holds(week.start, week.span),
        nonWorking: false,
      };
    });
    units = days.map((day, index) => ({
      key: day.iso,
      label: String(parseIso(day.iso).day),
      // „Mo.“ → „Mo“: the dot costs width in a narrow column and adds nothing.
      sublabel: weekday.format(utcDate(day.iso)).replace(/\.$/, ''),
      start: index,
      span: 1,
      current: index === todayIndex,
      nonWorking: day.nonWorking,
      ...(day.holiday ? { title: day.holiday } : {}),
    }));
  } else {
    const monthYear = formatter(locale, { month: 'long', year: 'numeric' });
    bands = runs(days, (day) => day.iso.slice(0, 7)).map((month) => ({
      key: month.key,
      label: monthYear.format(utcDate(days[month.start].iso)),
      start: month.start,
      span: month.span,
      current: holds(month.start, month.span),
      nonWorking: false,
    }));
    units = weeks.map((week) => ({
      key: week.key,
      label: weekLabel(isoWeekOf(days[week.start].iso)),
      // The Monday names the week, even when the range starts later in it.
      sublabel: dayMonth.format(utcDate(week.key)),
      start: week.start,
      span: week.span,
      current: holds(week.start, week.span),
      nonWorking: false,
    }));
  }

  const nonWorkingRuns: { start: number; span: number }[] = [];
  days.forEach((day, index) => {
    if (!day.nonWorking) return;
    const last = nonWorkingRuns[nonWorkingRuns.length - 1];
    if (last && last.start + last.span === index) last.span += 1;
    else nonWorkingRuns.push({ start: index, span: 1 });
  });

  return {
    start: days[0].iso,
    end: days[days.length - 1].iso,
    scale,
    days,
    bands,
    units,
    nonWorkingRuns,
    weekStarts: weeks.slice(1).map((week) => week.start),
    todayIndex,
  };
}

// ── Rows, bars, milestones ────────────────────────────────────────────────────

/** Rows under their group headers. Rows without a known group form the first section. */
export interface TimeBarSection {
  group: NxTimeBarGroup | null;
  rows: NxTimeBarRow[];
}

export function groupTimeBarRows(
  rows: readonly NxTimeBarRow[],
  groups: readonly NxTimeBarGroup[] = []
): TimeBarSection[] {
  const known = new Set<NxTimeBarKey>(groups.map((group) => group.id));
  const loose = rows.filter((row) => row.groupId == null || !known.has(row.groupId));
  const sections: TimeBarSection[] = loose.length ? [{ group: null, rows: loose }] : [];
  for (const group of groups) {
    sections.push({ group, rows: rows.filter((row) => row.groupId === group.id) });
  }
  return sections;
}

/** The days of an item, in order; `null` when its start is no date. */
export function itemDays(item: Pick<NxTimeBarItem, 'start' | 'end'>): NxTimeBarRange | null {
  const start = toIsoDay(item.start);
  if (!start) return null;
  const end = toIsoDay(item.end) ?? start;
  return daysBetween(start, end) < 0 ? { start: end, end: start } : { start, end };
}

export interface TimeBarPlacedItem extends NxTimeBarRange {
  item: NxTimeBarItem;
  /** First visible day column, 0-based. */
  column: number;
  /** Visible day columns. */
  span: number;
  /** Stacking lane inside the row, 0-based. */
  lane: number;
  /** The item begins before the visible range. */
  clippedStart: boolean;
  /** The item ends after the visible range. */
  clippedEnd: boolean;
}

/**
 * Place one row's items on the visible days and give overlapping ones their own
 * lane (greedy, in start order), so two bars on the same days sit one above the
 * other. Items outside the range or without a valid start are left out; items
 * reaching past an edge are cut there and flagged.
 */
export function placeTimeBarItems(
  items: readonly NxTimeBarItem[],
  rangeStart: string,
  dayCount: number
): { placed: TimeBarPlacedItem[]; lanes: number } {
  const positioned: Omit<TimeBarPlacedItem, 'lane'>[] = [];
  for (const item of items) {
    const days = itemDays(item);
    if (!days) continue;
    const from = daysBetween(rangeStart, days.start);
    const to = daysBetween(rangeStart, days.end);
    if (to < 0 || from >= dayCount) continue;
    const column = Math.max(0, from);
    positioned.push({
      item,
      ...days,
      column,
      span: Math.min(dayCount - 1, to) - column + 1,
      clippedStart: from < 0,
      clippedEnd: to >= dayCount,
    });
  }
  // Stable: items starting on the same day keep the caller's order.
  positioned.sort((a, b) => a.column - b.column);

  /** Per lane: the first free column. */
  const laneEnds: number[] = [];
  const placed = positioned.map((entry) => {
    let lane = laneEnds.findIndex((free) => free <= entry.column);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = entry.column + entry.span;
    return { ...entry, lane };
  });
  return { placed, lanes: Math.max(1, laneEnds.length) };
}

export interface TimeBarPlacedMilestone {
  milestone: NxTimeBarMilestone;
  date: string;
  column: number;
}

/** Visible milestones with their day column, in date order. */
export function placeTimeBarMilestones(
  milestones: readonly NxTimeBarMilestone[],
  rangeStart: string,
  dayCount: number
): TimeBarPlacedMilestone[] {
  const placed: TimeBarPlacedMilestone[] = [];
  for (const milestone of milestones) {
    const date = toIsoDay(milestone.date);
    if (!date) continue;
    const column = daysBetween(rangeStart, date);
    if (column >= 0 && column < dayCount) placed.push({ milestone, date, column });
  }
  return placed.sort((a, b) => a.column - b.column);
}

/** Where a group's content lies: visible part and real days. */
export interface TimeBarExtent extends NxTimeBarRange {
  column: number;
  span: number;
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * The span of everything under a group header (its rows' items and all
 * milestones), cut to the visible range. `null` when nothing dated is there or
 * it all lies outside.
 */
export function timeBarExtent(
  items: readonly NxTimeBarItem[],
  milestones: readonly NxTimeBarMilestone[],
  rangeStart: string,
  dayCount: number
): TimeBarExtent | null {
  const spans: NxTimeBarRange[] = [];
  for (const item of items) {
    const days = itemDays(item);
    if (days) spans.push(days);
  }
  for (const milestone of milestones) {
    const date = toIsoDay(milestone.date);
    if (date) spans.push({ start: date, end: date });
  }
  if (!spans.length) return null;
  let { start, end } = spans[0];
  for (const span of spans) {
    if (daysBetween(span.start, start) > 0) start = span.start;
    if (daysBetween(end, span.end) > 0) end = span.end;
  }
  const from = daysBetween(rangeStart, start);
  const to = daysBetween(rangeStart, end);
  if (to < 0 || from >= dayCount) return null;
  const column = Math.max(0, from);
  return {
    start,
    end,
    column,
    span: Math.min(dayCount - 1, to) - column + 1,
    clippedStart: from < 0,
    clippedEnd: to >= dayCount,
  };
}

// ── Accessible names ──────────────────────────────────────────────────────────

/** „Datenmapping, 8 h, 01.10.–05.10.2026, In Arbeit, Verzug“. */
export function timeBarItemLabel(
  item: NxTimeBarItem,
  days: NxTimeBarRange,
  locale: string,
  delayedLabel: string
): string {
  const parts = [item.label];
  if (item.text && item.text !== item.label) parts.push(item.text);
  parts.push(formatTimeBarRange(days.start, days.end, locale));
  if (item.detail) parts.push(item.detail);
  if (item.delayed) parts.push(delayedLabel);
  return parts.join(', ');
}

/** „Go-Live, Meilenstein, 15.09.2026, verpasst“. */
export function timeBarMilestoneLabel(
  milestone: NxTimeBarMilestone,
  date: string,
  locale: string,
  labels: { milestone: string; missed: string; done: string }
): string {
  const parts = [milestone.label, labels.milestone, formatTimeBarRange(date, date, locale)];
  if (milestone.detail) parts.push(milestone.detail);
  if (milestone.missed) parts.push(labels.missed);
  else if (milestone.done) parts.push(labels.done);
  return parts.join(', ');
}

// ── Keyboard ──────────────────────────────────────────────────────────────────

/** A focusable mark (bar or diamond) in reading order: its row and day column. */
export interface TimeBarFocusStop {
  row: string;
  column: number;
}

/**
 * Where a key moves the focus from `stops[from]`. `stops` is in reading order
 * (rows top to bottom, each row by time). Left/Right step through the row;
 * Up/Down jump to the neighbouring row that has marks and land on the one
 * nearest in time; Home/End go to the row's first/last mark. `null` = stay.
 */
export function timeBarFocusTarget(
  stops: readonly TimeBarFocusStop[],
  from: number,
  key: string
): number | null {
  const current = stops[from];
  if (!current) return null;
  const inRow = (index: number) => stops[index]?.row === current.row;
  switch (key) {
    case 'ArrowRight':
      return inRow(from + 1) ? from + 1 : null;
    case 'ArrowLeft':
      return inRow(from - 1) ? from - 1 : null;
    case 'Home':
    case 'End': {
      const step = key === 'Home' ? -1 : 1;
      let index = from;
      while (inRow(index + step)) index += step;
      return index === from ? null : index;
    }
    case 'ArrowDown':
    case 'ArrowUp': {
      const step = key === 'ArrowDown' ? 1 : -1;
      let index = from;
      while (inRow(index)) index += step;
      const target = stops[index];
      if (!target) return null;
      let best = index;
      for (let i = index; stops[i]?.row === target.row; i += step) {
        const distance = Math.abs(stops[i].column - current.column);
        const bestDistance = Math.abs(stops[best].column - current.column);
        // On a tie the earlier mark wins, whichever way the scan runs.
        if (distance < bestDistance || (distance === bestDistance && i < best)) best = i;
      }
      return best;
    }
    default:
      return null;
  }
}
